import { useEffect, useState } from 'react';
import { RiTwitterXFill } from 'react-icons/ri';
import { FaLinkedinIn } from 'react-icons/fa';

interface AuthorBioProps {
  authorSlug?: string;
}

interface WPUser {
  id: number;
  name: string;
  description: string;
  link: string;
  slug: string;
  avatar_urls: Record<string, string>;
  acf?: {
    full_name?: string;
    user_role?: string;
    custom_avatar?: number | string;
    twitter?: string;
    linkedin?: string;
    bio?: string;
  };
}

// Decode HTML entities from WP
function decodeHtml(html: string) {
  const txt = document.createElement('textarea');
  txt.innerHTML = html;
  return txt.value;
}

// Fetch attachment (custom avatar)
async function getAttachmentUrl(id: number | string) {
  try {
    const res = await fetch(`https://blog.thecryptotutor.com/wp-json/wp/v2/media/${id}`);
    const data = await res.json();
    return data.source_url || '/default-author.jpg';
  } catch {
    return '/default-author.jpg';
  }
}

export default function AuthorBio({ authorSlug }: AuthorBioProps) {
  const [author, setAuthor] = useState<WPUser | null>(null);
  const [avatarUrl, setAvatarUrl] = useState<string>('/default-author.jpg');

  useEffect(() => {
    if (!authorSlug) return;

    fetch(`https://blog.thecryptotutor.com/wp-json/wp/v2/users?slug=${authorSlug}`)
      .then(res => res.json())
      .then(async (data: WPUser[]) => {
        if (!data || data.length === 0) return;
        const user = data[0];
        setAuthor(user);

        // Custom avatar from ACF
        if (user.acf?.custom_avatar) {
          const url = await getAttachmentUrl(user.acf.custom_avatar);
          setAvatarUrl(url);
        } else {
          setAvatarUrl(
            user.avatar_urls['96'] ||
            user.avatar_urls['48'] ||
            user.avatar_urls['24'] ||
            '/default-author.jpg'
          );
        }
      })
      .catch(err => console.error('Failed to fetch author:', err));
  }, [authorSlug]);

  if (!author) return null;

  const displayName = author.acf?.full_name?.trim() || author.name;
  const role = author.acf?.user_role || '';
  const bio = decodeHtml(author.acf?.bio || author.description || '');
  const twitter = author.acf?.twitter || '';
  const linkedin = author.acf?.linkedin || '';

  return (
    <div className="bg-[#FFEEF0] rounded-lg p-6 sm:p-8 shadow-sm w-full">
      
      {/* Avatar + Info */}
      <div className="flex flex-col sm:flex-row items-center sm:items-start space-y-4 sm:space-y-0 sm:space-x-6 mb-6">
        
        <div className="flex-shrink-0">
          <img
            src={avatarUrl}
            alt={displayName}
            className="w-20 h-20 sm:w-24 sm:h-24 rounded-lg object-cover"
          />
        </div>

        <div className="flex-1 text-center sm:text-left">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-1">
            {displayName}
          </h1>
          {role && (
            <p className="text-red-500 font-semibold text-base sm:text-lg mb-4">
              {role}
            </p>
          )}

          {/* Social icons only */}
          <div className="flex flex-wrap justify-center sm:justify-start items-center gap-3">

            {/* X/Twitter */}
            {twitter && (
              <a
                href={
                  twitter.startsWith('http')
                    ? twitter
                    : `https://twitter.com/${twitter.replace('@', '')}`
                }
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 bg-black rounded-lg flex items-center justify-center hover:opacity-80 transition"
              >
                <RiTwitterXFill className="w-4 h-4 text-white" />
              </a>
            )}

            {/* LinkedIn */}
            {linkedin && (
              <a
                href={
                  linkedin.startsWith('http')
                    ? linkedin
                    : `https://linkedin.com/in/${linkedin}`
                }
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 bg-blue-700 rounded-lg flex items-center justify-center hover:opacity-80 transition"
              >
                <FaLinkedinIn className="w-4 h-4 text-white" />
              </a>
            )}

          </div>
        </div>
      </div>

      {/* Bio */}
      {bio && (
        <p className="text-gray-700 leading-relaxed text-base sm:text-lg">
          {bio}
        </p>
      )}
    </div>
  );
}
